import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import AppLayout from './components/Layout/Layout';
import LandingLayout from './components/Layout/LandingLayout';
import ScrollToTop from './components/ScrollToTop';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import TestCasesPage from './pages/TestCasesPage';
import Profile from './pages/Profile';
import DocumentationPage from './pages/Documentation';
import DocumentationView from './pages/DocumentationView';
import TestRunsPage from './pages/TestRunsPage';
import TestRunPage from './pages/TestRunPage';
import TestRunExecutionPage from './pages/TestRunExecutionPage';
import ReportsPage from './pages/ReportsPage';
import BugsPage from './pages/BugsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import TestItemVersions from './pages/TestItemVersions';
import DocumentationProjectSelect from './pages/DocumentationProjectSelect';
import ForgotPassword from './pages/ForgotPassword';

// Landing pages
import HomePage from './pages/landing/HomePage';
import AboutPage from './pages/landing/AboutPage';
import PricingPage from './pages/landing/PricingPage';
import ContactPage from './pages/landing/ContactPage';
import PrivacyPage from './pages/landing/PrivacyPage';
import TermsPage from './pages/landing/TermsPage';
import FeaturesPage from './pages/landing/FeaturesPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    // Initialize language from localStorage or use default (ru)
    const savedLanguage = localStorage.getItem('language') || 'ru';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  return (
    <>
      <HelmetProvider>
        <ScrollToTop />
        <Routes>
          {/* Landing pages */}
          <Route element={<LandingLayout><Outlet /></LandingLayout>}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/features" element={<FeaturesPage />} />
          </Route>

          {/* Auth pages */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Protected app routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="test-cases" element={<TestCasesPage />} />
            <Route path="test-cases/:projectId" element={<TestCasesPage />} />
            <Route path="documentation" element={<DocumentationProjectSelect />} />
            <Route path="documentation/project/:projectId" element={<DocumentationPage />} />
            <Route path="documentation/:id" element={<DocumentationView />} />
            <Route path="test-runs" element={<TestRunsPage />} />
            <Route path="test-runs/:id" element={<TestRunPage />} />
            <Route path="test-runs/:id/execute" element={<TestRunExecutionPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="bugs" element={<BugsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="projects/:projectId/test-items/:testItemId/versions" element={<TestItemVersions />} />
          </Route>

          {/* Redirect old routes to new app routes */}
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" />} />
          <Route path="/projects" element={<Navigate to="/app/projects" />} />
          <Route path="/test-cases" element={<Navigate to="/app/test-cases" />} />
          <Route path="/documentation" element={<Navigate to="/app/documentation" />} />
          <Route path="/test-runs" element={<Navigate to="/app/test-runs" />} />
          <Route path="/reports" element={<Navigate to="/app/reports" />} />
          <Route path="/bugs" element={<Navigate to="/app/bugs" />} />
          <Route path="/users" element={<Navigate to="/app/users" />} />
          <Route path="/settings" element={<Navigate to="/app/settings" />} />
          <Route path="/profile" element={<Navigate to="/app/profile" />} />
        </Routes>
      </HelmetProvider>
    </>
  );
};

export default App;
